import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { determineUserType, isAdmin } from '@/utils/auth-helpers';
import { withTimeout } from '@/utils/asyncTimeout';
import { clearAllAuthState } from '@/utils/auth-cleanup';

// ─── Explicit status enums ───────────────────────────────────────────────────
export type AuthStatus = 'loading' | 'loggedOut' | 'loggedIn';
export type RoleStatus = 'loading' | 'resolved' | 'none';
export type UserRole = 'client' | 'practitioner' | 'admin' | 'host' | null;

type AuthContextType = {
  user: User | null;
  session: Session | null;
  /** @deprecated Use authStatus instead */
  isLoading: boolean;
  /** @deprecated Use roleStatus instead */
  roleReady: boolean;
  authStatus: AuthStatus;
  roleStatus: RoleStatus;
  activeRole: UserRole;
  signUp: (email: string, password: string, userType: 'client' | 'practitioner' | 'host', profileData: any) => Promise<{
    error: any | null;
    data: any | null;
  }>;
  signIn: (email: string, password: string, intendedRole?: UserRole) => Promise<{
    error: any | null;
    data: any | null;
  }>;
  signOut: () => Promise<void>;
  getUserType: () => Promise<'client' | 'practitioner' | 'admin' | 'host' | 'unknown'>;
  setActiveRole: (role: UserRole) => void;
  updatePassword: (newPassword: string) => Promise<{ error: any | null }>;
  resetPassword: (email: string, userType?: 'client' | 'practitioner') => Promise<{ error: any | null }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_ROLE_KEY = 'session_active_role';
const PASSWORD_RESET_REQUEST_TYPE_KEY = 'password_reset_requested_type';
const PASSWORD_RECOVERY_FLAG_KEY = 'password_recovery_in_progress';
const PASSWORD_RECOVERY_SESSION_TOKENS_KEY = 'password_recovery_session_tokens';

const clearPasswordResetArtifacts = () => {
  localStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
  localStorage.removeItem(PASSWORD_RECOVERY_SESSION_TOKENS_KEY);
  sessionStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
  sessionStorage.removeItem(PASSWORD_RECOVERY_FLAG_KEY);
  sessionStorage.removeItem(PASSWORD_RECOVERY_SESSION_TOKENS_KEY);

  try {
    document.cookie = 'password_reset_type=; domain=.resonow.org; path=/; max-age=0';
    document.cookie = 'password_reset_type=; path=/; max-age=0';
  } catch {
    // ignore cookie cleanup failures
  }
};

// ─── Shared role resolution logic (eliminates duplication) ───────────────────
async function resolveRole(
  userId: string,
  cachedRole: string | null,
  roleDetectionGuard: React.MutableRefObject<boolean>,
): Promise<UserRole> {
  // 1. If there's a cached role, validate it with a single RPC call
  if (cachedRole && cachedRole !== 'null') {
    const existingRole = cachedRole as UserRole;
    console.log('Validating cached session role:', existingRole);

    try {
      roleDetectionGuard.current = true;
      const { data, error } = await withTimeout(
        supabase.rpc('get_user_role', { p_user_id: userId }),
        12000
      );

      if (error) throw error;

      const actualRole = data as string;
      if (actualRole === existingRole) {
        console.log('Cached role validated:', existingRole);
        return existingRole;
      }

      // Role changed — update to actual
      if (actualRole !== 'unknown') {
        console.log('Cached role mismatch, updating to:', actualRole);
        sessionStorage.setItem(SESSION_ROLE_KEY, actualRole);
        return actualRole as UserRole;
      }

      console.warn('Cached role INVALID:', existingRole, '- actual:', actualRole);
      sessionStorage.removeItem(SESSION_ROLE_KEY);
    } catch (e: any) {
      console.error('Error validating cached role:', e);
      // On network/timeout errors, trust cache
      if (
        e?.message?.includes('abort') ||
        e?.message?.includes('Network') ||
        e?.message?.includes('timed out') ||
        e?.name === 'AbortError'
      ) {
        console.log('Network error, trusting cached role:', existingRole);
        return existingRole;
      }
      // Other errors — also trust cache to avoid blocking
      console.log('Unexpected error, trusting cached role:', existingRole);
      return existingRole;
    } finally {
      roleDetectionGuard.current = false;
    }
  }

  // 2. No valid cache — detect role from DB
  const preferredRole = localStorage.getItem('auth_intended_role') as UserRole;
  console.log('Detecting role, preferred:', preferredRole);

  const userType = await determineUserType(userId, preferredRole);
  console.log('Role detection result:', userType);

  localStorage.removeItem('auth_intended_role');

  if (userType !== 'unknown') {
    const finalRole = userType as UserRole;
    sessionStorage.setItem(SESSION_ROLE_KEY, finalRole);
    return finalRole;
  }

  // New user with a preferred role but no profile yet
  if (preferredRole) {
    sessionStorage.setItem(SESSION_ROLE_KEY, preferredRole);
    return preferredRole;
  }

  return null;
}

// ─── Provider ────────────────────────────────────────────────────────────────
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [roleStatus, setRoleStatus] = useState<RoleStatus>(() =>
    sessionStorage.getItem(SESSION_ROLE_KEY) ? 'resolved' : 'loading'
  );
  const cachedRoleInit = sessionStorage.getItem(SESSION_ROLE_KEY) as UserRole;
  const [activeRole, setActiveRole] = useState<UserRole>(cachedRoleInit || null);

  // Backward compat
  const isLoading = authStatus === 'loading';
  const roleReady = roleStatus !== 'loading';

  const roleDetectionInProgress = useRef(false);

  // Safety timeout: ensure loading never blocks forever
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (authStatus === 'loading' || roleStatus === 'loading') {
        console.warn('Auth safety timeout reached — forcing ready state');
        setAuthStatus(prev => prev === 'loading' ? 'loggedOut' : prev);
        setRoleStatus(prev => prev === 'loading' ? 'none' : prev);
      }
    }, 8000);
    return () => clearTimeout(timeout);
  }, [authStatus, roleStatus]);

  // ─── Handle auth user with role resolution ─────────────────────────────────
  const handleAuthUser = useCallback(async (newSession: Session) => {
    const userId = newSession.user.id;

    // Skip role detection during password recovery
    const recoveryFlag = sessionStorage.getItem('password_recovery_in_progress');
    if (recoveryFlag) {
      try {
        const parsed = JSON.parse(recoveryFlag);
        if (parsed.ts && Date.now() - parsed.ts < 10 * 60 * 1000) {
          console.log('Auth state during password recovery — skipping role detection');
          return;
        }
      } catch { /* ignore */ }
    }

    // Dedup guard
    if (roleDetectionInProgress.current) {
      console.log('Role detection already in progress, skipping');
      setAuthStatus('loggedIn');
      return;
    }

    // ── Immediately unblock UI ──────────────────────────────────────────────
    setSession(newSession);
    setUser(newSession.user);
    setAuthStatus('loggedIn');          // UI can render immediately

    const cachedRole = sessionStorage.getItem(SESSION_ROLE_KEY);

    // If we have a cached role, trust it immediately for fast render
    // then validate in background (non-blocking)
    if (cachedRole && cachedRole !== 'null') {
      setActiveRole(cachedRole as UserRole);
      setRoleStatus('resolved');

      // Background validation — don't block UI
      roleDetectionInProgress.current = true;
      resolveRole(userId, cachedRole, roleDetectionInProgress)
        .then(resolved => {
          if (resolved && resolved !== cachedRole) {
            setActiveRole(resolved);
            sessionStorage.setItem(SESSION_ROLE_KEY, resolved);
          }
        })
        .catch(err => console.warn('Background role validation failed:', err))
        .finally(() => { roleDetectionInProgress.current = false; });
    } else {
      // No cache — resolve in background, NOT blocking render.
      // roleStatus stays 'loading' until resolved so ProtectedRoute shows spinner,
      // but authStatus is already 'loggedIn' so the page doesn't redirect.
      roleDetectionInProgress.current = true;
      resolveRole(userId, cachedRole, roleDetectionInProgress)
        .then(resolved => {
          setActiveRole(resolved);
          setRoleStatus(resolved ? 'resolved' : 'none');
          if (resolved) {
            sessionStorage.setItem(SESSION_ROLE_KEY, resolved);
          }
        })
        .catch(error => {
          console.error('Error in role resolution:', error);
          setActiveRole(null);
          setRoleStatus('none');
        })
        .finally(() => {
          roleDetectionInProgress.current = false;
        });
    }
  }, []);

  // ─── Handle signed out ─────────────────────────────────────────────────────
  const handleSignedOut = useCallback(() => {
    clearAllAuthState();
    setUser(null);
    setSession(null);
    setActiveRole(null);
    setAuthStatus('loggedOut');
    setRoleStatus('none');
  }, []);

  // ─── Initialize auth ──────────────────────────────────────────────────────
  useEffect(() => {
    // 1. Set up listener FIRST (per Supabase docs)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event, newSession?.user?.id);

        if (newSession?.user) {
          await handleAuthUser(newSession);
        } else {
          handleSignedOut();
        }
      }
    );

    // 2. Then check existing session
    supabase.auth.getSession().then(async ({ data: { session: existingSession } }) => {
      if (existingSession?.user) {
        await handleAuthUser(existingSession);
      } else {
        handleSignedOut();
      }
    });

    return () => subscription.unsubscribe();
  }, [handleAuthUser, handleSignedOut]);

  // ─── signUp ────────────────────────────────────────────────────────────────
  const signUp = async (email: string, password: string, userType: 'client' | 'practitioner' | 'host', profileData: any) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            user_type: userType,
            ...profileData,
          }
        }
      });

      if (error) {
        console.error('SignUp error:', error);
        return { error, data: null };
      }

      // Log consent
      if (data?.user?.id) {
        const { logConsent } = await import('@/lib/compliance-logger');
        const consentVersion = new Date().toISOString().split('T')[0];
        logConsent({ userId: data.user.id, userType, consentType: 'terms_of_service', consentVersion, action: 'granted' });
        logConsent({ userId: data.user.id, userType, consentType: 'privacy_policy', consentVersion, action: 'granted' });
      }

      return { error: null, data };
    } catch (error) {
      console.error('Unexpected signup error:', error);
      return { error, data: null };
    }
  };

  // ─── signIn ────────────────────────────────────────────────────────────────
  const signIn = async (email: string, password: string, intendedRole?: UserRole) => {
    try {
      // Aggressive cleanup: wipe all previous auth state before new login
      clearAllAuthState();

      // Set role BEFORE login
      if (intendedRole) {
        localStorage.setItem('auth_intended_role', intendedRole);
        sessionStorage.setItem(SESSION_ROLE_KEY, intendedRole);
        console.log('Set intended role BEFORE login:', intendedRole);
      }

      // Pre-validate role
      if (intendedRole) {
        try {
          const { data: roleCheck, error: roleError } = await supabase.rpc(
            'check_user_role_by_email',
            { user_email: email, check_role: intendedRole }
          );

          if (roleError) {
            console.error('Role validation error:', roleError);
          } else if (roleCheck && typeof roleCheck === 'object') {
            const checkResult = roleCheck as any;
            const userExists = checkResult.user_exists;
            const hasClient = checkResult.has_client_profile;
            const hasPractitioner = checkResult.has_practitioner_profile;

            if (userExists) {
              if (intendedRole === 'client' && !hasClient && hasPractitioner) {
                localStorage.removeItem('auth_intended_role');
                sessionStorage.removeItem(SESSION_ROLE_KEY);
                return {
                  error: { message: 'This email is not registered as a client account. Please use the practitioner login page or register a new client account.' },
                  data: null
                };
              } else if (intendedRole === 'practitioner' && !hasPractitioner && hasClient) {
                localStorage.removeItem('auth_intended_role');
                sessionStorage.removeItem(SESSION_ROLE_KEY);
                return {
                  error: { message: 'This email is not registered as a practitioner account. Please use the client login page or register a new practitioner account.' },
                  data: null
                };
              }
            }
          }
        } catch (error) {
          console.error('Error in role pre-validation:', error);
        }
      }

      // Proceed with actual login
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        console.error('SignIn error:', error);
        if (intendedRole) {
          localStorage.removeItem('auth_intended_role');
          sessionStorage.removeItem(SESSION_ROLE_KEY);
        }
        const rawMessage = error.message || '';
        const mappedMessage = /email not confirmed/i.test(rawMessage)
          ? 'Email not confirmed. Please check your inbox and click the verification link before logging in.'
          : 'Invalid email or password.';
        return { error: { message: mappedMessage }, data: null };
      }

      // For admin login, validate immediately
      if (intendedRole === 'admin' && data?.user) {
        const adminValid = await isAdmin(data.user.id);
        if (adminValid) {
          console.log('Admin role validated immediately after signIn');
          setActiveRole('admin');
          sessionStorage.setItem(SESSION_ROLE_KEY, 'admin');
          setRoleStatus('resolved');
        } else {
          console.warn('User is not an admin, clearing admin role');
          localStorage.removeItem('auth_intended_role');
          sessionStorage.removeItem(SESSION_ROLE_KEY);
          return { error: { message: 'This account does not have administrator privileges.' }, data: null };
        }
      }

      return { error: null, data };
    } catch (error) {
      console.error('Unexpected signin error:', error);
      localStorage.removeItem('auth_intended_role');
      sessionStorage.removeItem(SESSION_ROLE_KEY);
      return { error, data: null };
    }
  };

  // ─── signOut ───────────────────────────────────────────────────────────────
  const signOut = async () => {
    try {
      const signOutPromise = supabase.auth.signOut();
      const result = await Promise.race([
        signOutPromise,
        new Promise<{ error: Error }>((resolve) =>
          setTimeout(() => resolve({ error: new Error('SignOut timed out') }), 10000)
        ),
      ]);
      if (result?.error) {
        console.error('SignOut error or timeout:', result.error);
      }
    } catch (error) {
      console.error('Unexpected signout error:', error);
    }

    // Aggressive cleanup: wipe ALL auth artifacts
    clearAllAuthState();

    // Reset React state
    setUser(null);
    setSession(null);
    setActiveRole(null);
    setAuthStatus('loggedOut');
    setRoleStatus('none');
  };

  const getUserType = async (): Promise<'client' | 'practitioner' | 'admin' | 'host' | 'unknown'> => {
    if (!user) return 'unknown';
    return await determineUserType(user.id);
  };

  const setActiveRoleHandler = (role: UserRole) => {
    setActiveRole(role);
    if (role) {
      sessionStorage.setItem(SESSION_ROLE_KEY, role);
      setRoleStatus('resolved');
    } else {
      sessionStorage.removeItem(SESSION_ROLE_KEY);
      setRoleStatus('none');
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { error: null };
  };

  const resetPassword = async (email: string, userType?: 'client' | 'practitioner') => {
    try {
      const normalizedUserType = userType === 'practitioner' ? 'practitioner' : 'client';
      const requestTs = Date.now();
      const resetNonce = crypto.randomUUID();

      clearPasswordResetArtifacts();

      const resetRequestPayload = JSON.stringify({
        userType: normalizedUserType,
        ts: requestTs,
        nonce: resetNonce,
      });

      localStorage.setItem(PASSWORD_RESET_REQUEST_TYPE_KEY, resetRequestPayload);
      try {
        sessionStorage.setItem(PASSWORD_RESET_REQUEST_TYPE_KEY, resetRequestPayload);
      } catch {
        // ignore sessionStorage failures
      }
      try {
        document.cookie = `password_reset_type=${normalizedUserType}; domain=.resonow.org; path=/; max-age=3600; SameSite=Lax; Secure`;
      } catch { /* ignore */ }

      try {
        const { data: roleCheck, error: roleError } = await supabase.rpc(
          'check_user_role_by_email',
          { user_email: email, check_role: normalizedUserType }
        );

        if (!roleError && roleCheck && typeof roleCheck === 'object') {
          const checkResult = roleCheck as any;
          const userExists = checkResult.user_exists;
          const hasClient = checkResult.has_client_profile;
          const hasPractitioner = checkResult.has_practitioner_profile;

          if (userExists) {
            if (normalizedUserType === 'client' && !hasClient && hasPractitioner) {
              return { error: { message: 'This email is registered as a practitioner account. Please use practitioner forgot password.' } };
            }
            if (normalizedUserType === 'practitioner' && !hasPractitioner && hasClient) {
              return { error: { message: 'This email is registered as a client account. Please use client forgot password.' } };
            }
          }
        }
      } catch (precheckError) {
        console.warn('resetPassword role precheck failed:', precheckError);
      }

      const redirectUrl = new URL(`${window.location.origin}/reset-password`);
      redirectUrl.searchParams.set('type', normalizedUserType);
      redirectUrl.searchParams.set('reset_requested_at', String(requestTs));
      redirectUrl.searchParams.set('reset_nonce', resetNonce);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl.toString(),
      });

      if (error) {
        clearPasswordResetArtifacts();
      }

      return { error };
    } catch (error) {
      clearPasswordResetArtifacts();
      return { error };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    roleReady,
    authStatus,
    roleStatus,
    activeRole,
    signUp,
    signIn,
    signOut,
    getUserType,
    setActiveRole: setActiveRoleHandler,
    updatePassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
